import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "@/context/LangContext";

const Step = ({ index, t, total }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 30%"],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0.25, 1, 1, 0.5]);
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const scale = useTransform(scrollYProgress, [0, 0.4, 1], [0.96, 1, 0.98]);

  return (
    <motion.div
      ref={ref}
      style={{ opacity, y, scale }}
      data-testid={`how-step-${index}`}
      className="relative py-16 md:py-24 border-t border-white/[0.06] first:border-t-0"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-2">
          <div className="num-glyph text-[88px] md:text-[120px] leading-none font-serif-it">
            {String(index + 1).padStart(2, "0")}
          </div>
        </div>
        <div className="md:col-span-10 max-w-3xl">
          <div className="text-[11px] tracking-[0.3em] uppercase text-po-accent mb-4">
            Step {index + 1} / {total}
          </div>
          <h3 className="font-display text-white text-3xl md:text-5xl tracking-[-0.025em] font-medium leading-[1.05]">
            {t.t}
          </h3>
          <p className="mt-6 text-po-text2 text-base md:text-lg leading-relaxed max-w-2xl">
            {t.d}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const HowItWorks = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="how" ref={ref} className="relative py-24 md:py-32 overflow-hidden">
      <div
        className="glow-orb"
        style={{ width: 500, height: 500, background: "rgba(16,185,129,0.10)", top: "30%", left: "-10%" }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Sticky left title */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="text-[11px] tracking-[0.3em] uppercase text-po-accent mb-6">
                  {t.howItWorks.eyebrow}
                </div>
                <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl tracking-[-0.03em] font-semibold leading-[1.02]">
                  {t.howItWorks.title}
                  <br />
                  <span className="font-serif-it text-po-text2">{t.howItWorks.titleItalic}</span>
                </h2>

                {/* Progress rail */}
                <div className="hidden lg:block mt-12 relative h-[300px] w-px bg-white/5">
                  <motion.div
                    style={{ height: lineHeight }}
                    className="absolute top-0 left-0 w-px bg-gradient-to-b from-po-accent via-po-accent to-transparent shadow-[0_0_12px_rgba(16,185,129,0.6)]"
                  />
                </div>
              </motion.div>
            </div>
          </div>

          {/* Right scrolling steps */}
          <div className="lg:col-span-8">
            {t.howItWorks.steps.map((s, i) => (
              <Step key={i} index={i} t={s} total={t.howItWorks.steps.length} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
